import Button from "@mui/material/Button";
import React, { useContext, useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthAPI } from "../../api/AuthAPI";
import { AuthContext } from "./AuthContext";
import otpImg from '../../assets/images/otp.png';
import ReCAPTCHA from "react-google-recaptcha";
import {
  activationFailureMessage,
  isEmailVerificationLink,
  isValidVerificationEmail,
  isValidVerificationToken,
  parseEmailVerificationSearch,
} from "./emailVerification";

// Custom VerificationInput with paste support
const VerificationInput = ({ length = 6, onChange, autoFocus = false }) => {
  const [values, setValues] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, value) => {
    if (value.length > 1) return;

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    const fullCode = newValues.join('');
    onChange(fullCode);

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').slice(0, length);
    const pasteArray = pasteData.split('');

    const newValues = [...values];
    pasteArray.forEach((char, index) => {
      if (index < length && char.match(/[0-9]/)) {
        newValues[index] = char;
      }
    });

    setValues(newValues);
    const fullCode = newValues.join('');
    onChange(fullCode);

    const lastFilledIndex = newValues.findIndex(val => val === '');
    const focusIndex = lastFilledIndex === -1 ? length - 1 : Math.max(0, lastFilledIndex);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div
      style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}
      onPaste={handlePaste}
    >
      {values.map((value, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={value}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          style={{
            width: '50px',
            height: '60px',
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: '500',
            border: value ? '2px solid #1976d2' : '2px solid #ddd',
            borderRadius: '8px',
            outline: 'none',
            backgroundColor: value ? '#f0f7ff' : 'white',
            transition: 'all 0.2s ease',
            color: '#1a1a1a'
          }}
        />
      ))}
    </div>
  );
};

const MemberVerification = () => {
  const { signIn, isLoading, setMessage, setIsLoading } = useContext(AuthContext);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [code, setCode] = useState("");

  const stateObj = location?.state;
  const link = parseEmailVerificationSearch(searchParams.toString());
  const emailLinkReady = isEmailVerificationLink(`?${searchParams.toString()}`);
  const emailLinkPresent = Boolean(searchParams.get("email") || searchParams.get("token"));
  const emailLinkInvalid = emailLinkPresent && !emailLinkReady;

  useEffect(() => {
    if (!emailLinkPresent && !stateObj?.userName) {
      navigate("/login");
    }
  }, [emailLinkPresent, navigate, stateObj?.userName]);

  const confirmAccount = async (userName, token) => {
    if (!isValidVerificationEmail(userName) || !isValidVerificationToken(token)) {
      setMessage({ type: 'error', content: 'This confirmation link is invalid or incomplete.' });
      return;
    }

    try {
      setIsLoading(true);
      setMessage({ type: '', content: '' });
      await AuthAPI.confirmSignUp({
        userName,
        token,
      });
      setIsLoading(false);
      setMessage({
        type: 'success',
        content: 'Your email is verified. Open the app and log in.',
      });
      navigate("/login", { replace: true });
    } catch (err) {
      setIsLoading(false);
      setMessage({
        type: 'error',
        content: activationFailureMessage(err),
      });
    }
  };

  const login = async () => {
    await signIn(stateObj?.userName?.trim(), stateObj?.password?.trim());
    if (stateObj?.token) {
      navigate("/events");
    } else {
      navigate("/");
    }
  };

  const handleActivateMember = async (e) => {
    e.preventDefault();
    setMessage({ type: '', content: '' });
    const currentUser = stateObj?.userName;

    if (!code || code?.length < 6) {
      setMessage({ type: 'error', content: 'Please enter the 6-digit verification code' });
      return;
    }

    if (!isValidVerificationToken(code)) {
      setMessage({ type: 'error', content: 'Confirmation code must be 6 digits' });
      return;
    }

    if (!captchaVerified) {
      setMessage({ type: 'error', content: 'Please complete the CAPTCHA verification' });
      return;
    }

    try {
      setIsLoading(true);
      await AuthAPI.confirmSignUp({
        token: code,
        userName: currentUser
      });
      setIsLoading(false);
      setMessage({ type: 'success', content: 'Your email is verified. Please log in' });

      if (stateObj?.token) {
        login();
      } else {
        navigate("/login", { replace: true });
      }
    } catch (err) {
      setIsLoading(false);
      setMessage({ type: 'error', content: activationFailureMessage(err) });
    }
  };

  const verifyCaptcha = async (data) => {
    try {
      const response = await AuthAPI.postVerifyCaptcha(data);
      setCaptchaVerified(response.Data);
    } catch (error) {
      setCaptchaVerified(false);
    }
  };

  if (emailLinkInvalid) {
    return (
      <div className="otp_verification_container">
        <div className="otp_verification_field">
          <img src={otpImg} alt="OTP Verification" />
          <h2>Verify Your Email</h2>
          <p>This confirmation link is invalid or incomplete.</p>
        </div>
      </div>
    );
  }

  if (emailLinkReady) {
    return (
      <div className="otp_verification_container">
        <div className="otp_verification_field">
          <img src={otpImg} alt="OTP Verification" />
          <h2>Verify Your Email</h2>
          <p>
            Confirm <span>{link.email}</span> to activate this SportMingle account.
            We wait for this click so email scanners cannot use the link by themselves.
          </p>
          <div className="otp_validate_btn">
            <Button
              type="button"
              fullWidth
              disabled={isLoading}
              variant="contained"
              onClick={() => confirmAccount(link.email, link.token)}
            >
              {isLoading ? 'Verifying...' : 'Activate account'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!stateObj?.userName) {
    return null;
  }

  return (
    <div className="otp_verification_container">
      <div className="otp_verification_field">
        <img src={otpImg} alt="OTP Verification" />
        <h2>Verify Your Email</h2>
        <p>
          A confirmation code has been sent to your email address <span>{stateObj?.userName}</span>.
          To activate the account and validate your email, enter the code below.
        </p>
        <form className="otp_validate_input" noValidate>
          <VerificationInput
            autoFocus={true}
            length={6}
            onChange={setCode}
          />

          <ReCAPTCHA
            style={{ alignSelf: 'center', marginTop: '15px' }}
            className="re-captcha"
            sitekey={`${import.meta.env.VITE_APP_CAPTCHA_SITE_KEY}`}
            onChange={verifyCaptcha}
          />

          <div className="otp_validate_btn">
            <Button
              type="submit"
              fullWidth
              disabled={!captchaVerified || isLoading}
              variant="contained"
              onClick={handleActivateMember}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberVerification;
